import React, { useState, useEffect, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Transition } from '@headlessui/react'
import { Button, Text, Select, SelectItem } from '@tremor/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { sanitizeUserInput } from '../../utils/security'

function MethodologyFormModal({ isOpen, onClose, onSubmit, editMethodology = null, loading = false }) {
    const { t } = useTranslation()
    const isEditing = !!editMethodology
    const [formData, setFormData] = useState({
        methodology_type: '',
        research_design: '',
        data_collection: '',
        analysis_approach: '',
        reasoning: '',
        status: 'draft'
    })
    const [errors, setErrors] = useState({})
    const [submitError, setSubmitError] = useState('')

    useEffect(() => {
        if (editMethodology) {
            setFormData({
                methodology_type: editMethodology.methodology_type || '',
                research_design: editMethodology.research_design || '',
                data_collection: editMethodology.data_collection || '',
                analysis_approach: editMethodology.analysis_approach || '',
                reasoning: editMethodology.reasoning || '',
                status: editMethodology.status || 'draft'
            })
        } else {
            // Reset form for new methodology
            setFormData({
                methodology_type: '',
                research_design: '',
                data_collection: '',
                analysis_approach: '',
                reasoning: '',
                status: 'draft'
            })
        }
        setErrors({})
        setSubmitError('')
    }, [editMethodology])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }))
        }
    }

    const validateForm = () => {
        const newErrors = {}

        // Methodology Type validation
        if (!formData.methodology_type?.trim()) {
            newErrors.methodology_type = t('Research.Methodology.Validation.MethodologyTypeRequired')
        }

        // Research Design validation (minimum 20 characters)
        if (!formData.research_design?.trim()) {
            newErrors.research_design = t('Research.Methodology.Validation.ResearchDesignRequired')
        } else if (formData.research_design.trim().length < 20) {
            newErrors.research_design = t('Research.Methodology.Validation.ResearchDesignTooShort', { min: 20 })
        }

        // Data Collection validation (minimum 20 characters)
        if (!formData.data_collection?.trim()) {
            newErrors.data_collection = t('Research.Methodology.Validation.DataCollectionRequired')
        } else if (formData.data_collection.trim().length < 20) {
            newErrors.data_collection = t('Research.Methodology.Validation.DataCollectionTooShort', { min: 20 })
        }

        // Analysis Approach validation (minimum 20 characters)
        if (!formData.analysis_approach?.trim()) {
            newErrors.analysis_approach = t('Research.Methodology.Validation.AnalysisApproachRequired')
        } else if (formData.analysis_approach.trim().length < 20) {
            newErrors.analysis_approach = t('Research.Methodology.Validation.AnalysisApproachTooShort', { min: 20 })
        }

        // Reasoning validation (optional, but if provided minimum 10 characters)
        if (formData.reasoning && formData.reasoning.trim().length > 0 && formData.reasoning.trim().length < 10) {
            newErrors.reasoning = t('Research.Methodology.Validation.ReasoningTooShort', { min: 10 })
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }

        try {
            const cleanData = {
                methodology_type: sanitizeUserInput(formData.methodology_type),
                research_design: sanitizeUserInput(formData.research_design),
                data_collection: sanitizeUserInput(formData.data_collection),
                analysis_approach: sanitizeUserInput(formData.analysis_approach),
                reasoning: formData.reasoning ? sanitizeUserInput(formData.reasoning) : '',
                status: formData.status,
                type: 'manual'
            }

            await onSubmit(cleanData)
            handleClose()
        } catch (err) {
            console.error('Failed to submit methodology:', err)
            setSubmitError(err.message || t('Research.Methodology.SubmitError'))
        }
    }

    const handleClose = () => {
        if (!loading) {
            setFormData({
                methodology_type: '',
                research_design: '',
                data_collection: '',
                analysis_approach: '',
                reasoning: '',
                status: 'draft'
            })
            setErrors({})
            setSubmitError('')
            onClose()
        }
    }

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex justify-between items-center mb-6">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                        {isEditing 
                                            ? t('Research.Methodology.EditMethodology')
                                            : t('Research.Methodology.CreateMethodology')
                                        }
                                    </Dialog.Title>
                                    <button
                                        onClick={handleClose}
                                        disabled={loading}
                                        className="rounded-md p-2 hover:bg-gray-100"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {submitError && (
                                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                            <Text className="text-red-600 text-sm">{submitError}</Text>
                                        </div>
                                    )}

                                    {/* Methodology Type */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Research.Methodology.MethodologyType')} *
                                        </label>
                                        <Select value={formData.methodology_type} onValueChange={(value) => handleInputChange('methodology_type', value)} disabled={loading}>
                                            <SelectItem value="qualitative">{t('Research.Methodology.Types.qualitative')}</SelectItem>
                                            <SelectItem value="quantitative">{t('Research.Methodology.Types.quantitative')}</SelectItem>
                                            <SelectItem value="mixed">{t('Research.Methodology.Types.mixed')}</SelectItem>
                                        </Select>
                                        {errors.methodology_type && (
                                            <Text className="text-red-500 text-sm mt-1">
                                                {errors.methodology_type}
                                            </Text>
                                        )}
                                    </div>

                        {/* Research Design */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Research.Methodology.ResearchDesign')}
                            </label>
                            <div className="relative">
                                <textarea
                                    rows={3}
                                    value={formData.research_design}
                                    onChange={(e) => handleInputChange('research_design', e.target.value)}
                                    placeholder={t('Research.Methodology.ResearchDesignPlaceholder')}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <Text className="absolute bottom-2 right-3 text-xs text-gray-400">
                                    {formData.research_design.length}
                                </Text>
                            </div>
                            <Text className="text-gray-500 text-xs mt-1">
                                {t('Research.Methodology.ResearchDesignHelp')}
                            </Text>
                            {errors.research_design && (
                                <Text className="text-red-500 text-sm mt-1">
                                    {errors.research_design}
                                </Text>
                            )}
                        </div>

                        {/* Data Collection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Research.Methodology.DataCollection')}
                            </label>
                            <div className="relative">
                                <textarea
                                    rows={3}
                                    value={formData.data_collection}
                                    onChange={(e) => handleInputChange('data_collection', e.target.value)}
                                    placeholder={t('Research.Methodology.DataCollectionPlaceholder')}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <Text className="absolute bottom-2 right-3 text-xs text-gray-400">
                                    {formData.data_collection.length}
                                </Text>
                            </div>
                            <Text className="text-gray-500 text-xs mt-1">
                                {t('Research.Methodology.DataCollectionHelp')}
                            </Text>
                            {errors.data_collection && (
                                <Text className="text-red-500 text-sm mt-1">
                                    {errors.data_collection}
                                </Text>
                            )}
                        </div>

                        {/* Analysis Approach */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Research.Methodology.AnalysisApproach')}
                            </label>
                            <div className="relative">
                                <textarea
                                    rows={3}
                                    value={formData.analysis_approach}
                                    onChange={(e) => handleInputChange('analysis_approach', e.target.value)}
                                    placeholder={t('Research.Methodology.AnalysisApproachPlaceholder')}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <Text className="absolute bottom-2 right-3 text-xs text-gray-400">
                                    {formData.analysis_approach.length}
                                </Text>
                            </div>
                            <Text className="text-gray-500 text-xs mt-1">
                                {t('Research.Methodology.AnalysisApproachHelp')}
                            </Text>
                            {errors.analysis_approach && (
                                <Text className="text-red-500 text-sm mt-1">
                                    {errors.analysis_approach}
                                </Text>
                            )}
                        </div>

                        {/* Reasoning */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Research.Methodology.Reasoning')} <span className="text-gray-500 text-sm">(optional)</span>
                            </label>
                            <div className="relative">
                                <textarea
                                    rows={4}
                                    value={formData.reasoning}
                                    onChange={(e) => handleInputChange('reasoning', e.target.value)}
                                    placeholder={t('Research.Methodology.ReasoningPlaceholder')}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                {formData.reasoning.length > 0 && (
                                    <Text className="absolute bottom-2 right-3 text-xs text-gray-400">
                                        {formData.reasoning.length}
                                    </Text>
                                )}
                            </div>
                            <Text className="text-gray-500 text-xs mt-1">
                                {t('Research.Methodology.ReasoningHelp')}
                            </Text>
                            {errors.reasoning && (
                                <Text className="text-red-500 text-sm mt-1">
                                    {errors.reasoning}
                                </Text>
                            )}
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Common.Status')}
                            </label>
                            <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                                <SelectItem value="draft">{t('Research.Methodology.Status.draft')}</SelectItem>
                                <SelectItem value="active">{t('Research.Methodology.Status.active')}</SelectItem>
                                <SelectItem value="archived">{t('Research.Methodology.Status.archived')}</SelectItem>
                            </Select>
                        </div>

                                    {/* Action Buttons */}
                                    <div className="flex justify-end gap-3 pt-4">
                                        <Button
                                            type="button"
                                            onClick={handleClose}
                                            disabled={loading}
                                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                        >
                                            {t('Common.Cancel')}
                                        </Button>
                                        <Button
                                            type="submit"
                                            loading={loading}
                                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                        >
                                            {isEditing ? t('Common.Update') : t('Common.Create')}
                                        </Button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default MethodologyFormModal